// TODO FIB-40547
// check if and how we can re-add tests after Osano is implemented
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { OsanoConsent, OsanoConsentValue } from '@app-types/osano';
import { isBrowser } from '@lib/environment/isBrowser';

export const OSANO_COOKIE_ACCEPTED = 'ACCEPT';
export const OSANO_COOKIE_DENIED = 'DENY';

interface OsanaCookiePermissions {
  ANALYTICS: OsanoConsentValue;
  ESSENTIAL: OsanoConsentValue;
  MARKETING: OsanoConsentValue;
  OPT_OUT: OsanoConsentValue;
  PERSONALIZATION: OsanoConsentValue;
  STORAGE: OsanoConsentValue;
}

export const NO_PERMISSIONS: OsanaCookiePermissions = {
  ANALYTICS: OSANO_COOKIE_DENIED,
  ESSENTIAL: OSANO_COOKIE_ACCEPTED,
  MARKETING: OSANO_COOKIE_DENIED,
  OPT_OUT: OSANO_COOKIE_DENIED,
  PERSONALIZATION: OSANO_COOKIE_DENIED,
  STORAGE: OSANO_COOKIE_DENIED,
};

interface IState {
  permissions: OsanaCookiePermissions;
}

const CookiePermissionsContext = createContext<IState | null>(null);
CookiePermissionsContext.displayName = 'CookiePermissionsContext';

interface IProps {
  children: ReactNode;
}

function CookiePermissionsProvider(props: IProps) {
  const [permissions, setPermissions] = useState<OsanaCookiePermissions>(NO_PERMISSIONS);

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    const update = (consent?: OsanoConsent) => {
      if (consent) {
        setPermissions(consent);
      }
    };

    window.Osano?.('onInitialized', update);
    window.Osano?.('onConsentSaved', update);

    return () => {
      // The command queue forwards our callback to cm.addEventListener
      // unwrapped, so the same reference removes it. No-ops if Osano hasn't
      // loaded yet (the registration is still queued and can't be cancelled).
      window.Osano?.cm?.removeEventListener('osano-cm-initialized', update);
      window.Osano?.cm?.removeEventListener('osano-cm-consent-saved', update);
    };
  }, []);

  return (
    <CookiePermissionsContext.Provider
      {...props}
      value={{
        permissions,
      }}
    />
  );
}

const useCookiePermissions = (): IState => {
  const context = useContext(CookiePermissionsContext);
  if (context === null) {
    throw new Error('useCookiePermissions must be used within a CookiePermissionsProvider');
  }

  return context;
};

export { CookiePermissionsProvider, useCookiePermissions };
